import { useState, useEffect, useMemo, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { Buildings, Shield, MagnifyingGlass, Truck, X, Key, UserSwitch, ArrowsClockwise, CircleNotch, CalendarBlank, CheckCircle, Clock, WarningCircle } from "@phosphor-icons/react";
import { format, parseISO } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FreshnessPill } from "@/components/FreshnessPill";
import { DateField } from "@/components/DateField";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";
import { usePullCooldown } from "@/hooks/usePullCooldown";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function timeAgo(isoString) {
  const diffMin = Math.max(0, Math.round((Date.now() - new Date(isoString).getTime()) / 60000));
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  return `${Math.round(diffMin / 60)}h ago`;
}

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  return String(detail);
}

const cartKey = (po) => `${po.po_number}::${po.item_number}`;

// Oct 27 2026 fix (real user report, same root cause as
// SupplierDashboardPage's fmtUnitPrice) - a per-unit rate can
// legitimately be a sub-cent fraction (SAP quotes some lines "per 100
// units"), so a flat 2-decimal rounding here made the line's own
// Open Qty x PO Price math visibly not add up.
function fmtUnitPrice(amount) {
  if (amount == null) return null;
  return Math.abs(amount) < 1 ? amount.toFixed(4) : amount.toFixed(2);
}

// Feb 2026, user's explicit ask ("Add item price... also show total of
// amount by the price*qty selected items") - a plain money AMOUNT
// (unlike a per-unit rate, which can legitimately need 4 decimals -
// see fmtUnitPrice above) always stays at 2 decimals.
function fmtMoney(amount, currency) {
  if (amount == null) return "-";
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: currency || "INR", maximumFractionDigits: 2 }).format(amount);
  } catch {
    return amount.toFixed(2);
  }
}

export default function ActAsSupplierPage() {
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountSearchOpen, setAccountSearchOpen] = useState(false);
  const [accountQuery, setAccountQuery] = useState("");

  const [pos, setPos] = useState([]);
  const [posLoading, setPosLoading] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [search, setSearch] = useState("");
  const [siteFilter, setSiteFilter] = useState("all");
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [liveSync, setLiveSync] = useState(true);

  // Oct 2026, user's explicit ask ("set default view 30 days... give a
  // date filter so he can view old PO also" - kept consistent with the
  // Supplier Dashboard, same underlying cache/endpoint).
  const isoDaysAgo = (days) => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().slice(0, 10);
  };
  const [fromDate, setFromDate] = useState(isoDaysAgo(30));
  const [toDate, setToDate] = useState(isoDaysAgo(0));
  const [cart, setCart] = useState({});
  const [reviewOpen, setReviewOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [successCode, setSuccessCode] = useState(null);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetting, setResetting] = useState(false);
  const activeAccountIdRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/admin/act-as-supplier/accounts`)
      .then(({ data }) => setAccounts(data.accounts || []))
      .catch((err) => toast.error("Could not load supplier accounts", { description: err?.response?.data?.detail || err.message }))
      .finally(() => setAccountsLoading(false));
  }, []);

  const accountMatches = useMemo(() => {
    const q = accountQuery.trim().toLowerCase();
    if (!q) return accounts.slice(0, 8);
    return accounts.filter((a) => a.vendor_code?.toLowerCase().includes(q) || a.company_name?.toLowerCase().includes(q) || a.email?.toLowerCase().includes(q)).slice(0, 8);
  }, [accountQuery, accounts]);

  const loadPos = async (accountId) => {
    // Oct 2026 fix (real incident - a Radish Technology PO with a missing
    // product master link ended up in a shipment created "on behalf of"
    // HAMIDI EXPORTS): switching suppliers quickly (select Radish -> its
    // GET is in flight -> select HAMIDI before it resolves) had no guard
    // against that FIRST request resolving LATE, after the second one -
    // its response would silently overwrite `pos` with Radish's own PO
    // list, while `selectedAccount` (and the dialog title) still
    // correctly showed HAMIDI EXPORTS. Only the LATEST-requested
    // account's response is now applied - any older one resolving late
    // is discarded.
    activeAccountIdRef.current = accountId;
    setPosLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/act-as-supplier/${accountId}/purchase-orders`, { params: { from_date: fromDate, to_date: toDate } });
      if (activeAccountIdRef.current !== accountId) return;
      setPos(data.purchase_orders || []);
      setLastSyncedAt(data.last_synced_at || null);
      setLiveSync(!!data.live_sync);
    } catch (err) {
      if (activeAccountIdRef.current !== accountId) return;
      toast.error("Could not load this supplier's Purchase Orders", { description: err?.response?.data?.detail || err.message });
      setPos([]);
    } finally {
      if (activeAccountIdRef.current === accountId) setPosLoading(false);
    }
  };

  const selectAccount = (account) => {
    setSelectedAccount(account);
    setAccountSearchOpen(false);
    setAccountQuery("");
    setCart({});
    setSearch("");
    setSiteFilter("all");
    setNewPassword("");
    setConfirmPassword("");
    loadPos(account._id);
  };

  // Reload (same account) whenever the date filter changes.
  useEffect(() => {
    if (selectedAccount) loadPos(selectedAccount._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  const { onCooldown, remainingLabel, startCooldown } = usePullCooldown("act-as-supplier-pull-cooldown", selectedAccount?.vendor_code);

  const pullLatestPos = async () => {
    if (!selectedAccount) return;
    const accountId = selectedAccount._id;
    setPulling(true);
    try {
      const { data } = await axios.post(`${API}/admin/act-as-supplier/${accountId}/purchase-orders/refresh`);
      startCooldown();
      toast.success(`Pulled the latest Purchase Orders from SAP (${data.po_count} on file).`);
      await loadPos(accountId);
    } catch (err) {
      if (err?.response?.status === 429) {
        const match = /wait (\d+) more minute/i.exec(err?.response?.data?.detail || "");
        startCooldown(match ? Number(match[1]) : undefined);
      }
      toast.error("Could not pull the latest POs from SAP", { description: err?.response?.data?.detail || err.message });
    } finally {
      setPulling(false);
    }
  };

  // Oct 29 2026, real incident (PO 30896, vendor M3530) - "Pull Latest
  // POs" never re-checks a PO number it has already "seen" before, even
  // one that got stuck "seen but never cached" from before the Oct 27
  // 2026 Not-Yet-Released fix. This bypasses that seen-set entirely for
  // just the one PO number typed in, so a specific missing-open-PO
  // report can be fixed immediately without a full vendor re-sweep.
  const [forceCheckOpen, setForceCheckOpen] = useState(false);
  const [forceCheckPoNumber, setForceCheckPoNumber] = useState("");
  const [forceChecking, setForceChecking] = useState(false);

  const forceCheckPo = async () => {
    if (!selectedAccount || !forceCheckPoNumber.trim()) return;
    setForceChecking(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/act-as-supplier/${selectedAccount._id}/purchase-orders/force-check`,
        null, { params: { po_number: forceCheckPoNumber.trim() } },
      );
      if (data.found) {
        toast.success(`PO ${data.po_number} is now on the list below.`);
        setForceCheckPoNumber("");
        setForceCheckOpen(false);
        await loadPos(selectedAccount._id);
      } else if (data.reason === "different_vendor") {
        toast.error(`PO ${data.po_number} belongs to a different supplier, not ${selectedAccount.company_name}.`);
      } else if (data.reason === "not_yet_released") {
        toast.error(`PO ${data.po_number} is in SAP but not yet released - it'll show up here once it is.`);
      } else {
        toast.error(`PO ${data.po_number} exists in SAP but isn't currently open (e.g. finished, cancelled or rejected).`);
      }
    } catch (err) {
      toast.error("Could not check that PO", { description: err?.response?.data?.detail || err.message });
    } finally {
      setForceChecking(false);
    }
  };

  // Oct 2026, user's explicit ask - "only show sites they have in PO"
  // in the filter dropdown, built from this account's own loaded POs.
  const sites = useMemo(() => {
    const set = new Set(pos.map((po) => po.ship_to_site_id).filter(Boolean));
    return Array.from(set).sort();
  }, [pos]);

  const filteredPos = useMemo(() => {
    const q = search.trim().toLowerCase();
    return pos
      .filter((po) => siteFilter === "all" || po.ship_to_site_id === siteFilter)
      .filter((po) => !q || [po.po_number, po.item_number, po.product_id, po.description].some((v) => (v || "").toString().toLowerCase().includes(q)));
  }, [pos, search, siteFilter]);

  // Oct 2026, user's explicit ask ("only 1 site PO per shipment code") -
  // once the cart holds an item, its Site locks the cart to that Site
  // only - every other Site's rows get a red "not usable" badge and a
  // disabled checkbox instead of a plain grey disable, so staff can see
  // AT A GLANCE which Site is currently active vs blocked.
  const lockedSiteId = useMemo(() => {
    const first = Object.values(cart)[0];
    return first?.ship_to_site_id || null;
  }, [cart]);

  const toggleCartItem = (po, checked) => {
    if (checked && !po.sap_verified_at) return; // Feb 2026 - mirrors backend hard-block in _resolve_items (SupplierDashboardPage has the same guard)
    setCart((prev) => {
      const next = { ...prev };
      const key = cartKey(po);
      if (checked) {
        next[key] = { po_number: po.po_number, item_number: po.item_number, product_id: po.product_id, description: po.description, unit_of_measure: po.unit_of_measure, remaining_qty: po.remaining_qty, extra_tolerance_pct: po.extra_tolerance_pct, ship_to_site_id: po.ship_to_site_id, unit_price: po.unit_price, currency: po.currency, ship_qty: "" };
      } else {
        delete next[key];
      }
      return next;
    });
  };

  const updateCartQty = (key, value) => setCart((prev) => ({ ...prev, [key]: { ...prev[key], ship_qty: value } }));
  const removeFromCart = (key) => setCart((prev) => { const next = { ...prev }; delete next[key]; return next; });
  const cartItems = Object.entries(cart).map(([key, v]) => ({ key, ...v }));
  // Feb 2026, user's explicit ask - total of price*qty across every
  // selected line, shown in the Review Shipment dialog.
  const cartTotal = cartItems.reduce((sum, c) => sum + (c.unit_price != null ? c.unit_price * (parseFloat(c.ship_qty) || 0) : 0), 0);
  const cartCurrency = cartItems.find((c) => c.currency)?.currency;

  const submitShipment = async () => {
    setSubmitting(true);
    setSubmitError("");
    try {
      const items = cartItems.map((c) => ({ po_number: c.po_number, item_number: c.item_number, ship_qty: parseFloat(c.ship_qty) }));
      const { data } = await axios.post(`${API}/admin/act-as-supplier/${selectedAccount._id}/shipments`, { items });
      setCart({});
      setConfirmOpen(false);
      setReviewOpen(false);
      setSuccessCode(data._id);
      loadPos(selectedAccount._id);
    } catch (err) {
      setSubmitError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const resetPassword = async () => {
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    setResetting(true);
    try {
      await axios.post(`${API}/admin/act-as-supplier/${selectedAccount._id}/reset-password`, { new_password: newPassword });
      toast.success(`New password emailed to ${selectedAccount.email}`);
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error("Could not reset password", { description: err?.response?.data?.detail || err.message });
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F6F7] font-sans" data-testid="act-as-supplier-page">
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <Shield size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Materials Hub</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">Act as Supplier</span>
          </div>
        </div>
        <div className="w-px h-7 bg-white/25 shrink-0" />
        <div className="flex items-center gap-3 flex-1 justify-start min-w-0">
          <NavTabs />
        </div>
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </header>

      <div className="max-w-[1400px] mx-auto p-4 md:p-6">
        <div className="flex items-center gap-2 mb-1">
          <UserSwitch size={18} weight="fill" className="text-[#0076CC]" />
          <h1 className="font-sans text-base font-bold text-[#111827]">Act as Supplier</h1>
        </div>
        <p className="text-sm text-[#5B738B]">Create a shipment on behalf of a supplier, or reset their portal password - every action here is tagged with your name for traceability.</p>

        <div className="mt-5 bg-white border border-[#CBD3DB] rounded-sm shadow-sm p-5 max-w-lg">
          <Label htmlFor="act-as-supplier-search" className="text-xs text-[#5B738B]">Select an Approved Supplier</Label>
          <Popover open={accountSearchOpen} onOpenChange={setAccountSearchOpen}>
            <PopoverTrigger asChild>
              <div className="relative mt-1">
                <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#98A2B3]" />
                <Input
                  id="act-as-supplier-search"
                  value={selectedAccount ? `${selectedAccount.vendor_code} - ${selectedAccount.company_name}` : accountQuery}
                  onChange={(e) => { setSelectedAccount(null); setAccountQuery(e.target.value); setAccountSearchOpen(true); }}
                  onFocus={() => setAccountSearchOpen(true)}
                  placeholder={accountsLoading ? "Loading suppliers..." : "Search vendor code, company or email..."}
                  className="rounded-sm border-[#CBD3DB] pl-8"
                  data-testid="act-as-supplier-search-input"
                  autoComplete="off"
                />
              </div>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
              <Command shouldFilter={false}>
                <CommandList data-testid="act-as-supplier-search-results">
                  {accountMatches.length === 0 && <CommandEmpty>No approved supplier matches.</CommandEmpty>}
                  <CommandGroup>
                    {accountMatches.map((a) => (
                      <CommandItem key={a._id} value={a._id} onSelect={() => selectAccount(a)} className="cursor-pointer" data-testid={`act-as-supplier-option-${a._id}`}>
                        <span className="font-data font-semibold text-[#111827] mr-2">{a.vendor_code}</span>
                        <span className="text-[#5B738B] truncate">{a.company_name} · {a.email}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {selectedAccount && (
          <>
            <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
              <h2 className="font-heading text-lg font-bold text-[#0F172A]">
                {selectedAccount.company_name}'s Open Purchase Orders
              </h2>
              <div className="flex items-center gap-3 flex-wrap">
                <Button
                  variant="outline"
                  onClick={pullLatestPos}
                  disabled={pulling || onCooldown}
                  className="h-8 rounded-sm border-[#E2E8F0] text-xs"
                  title={onCooldown ? "Pulled recently - wait for the 10 minute cooldown to finish." : "Don't want to wait? Pull the newest Purchase Orders from SAP right now."}
                  data-testid="act-as-supplier-pull-latest-pos-button"
                >
                  {pulling ? <CircleNotch size={13} className="mr-1.5 animate-spin" /> : <ArrowsClockwise size={13} className="mr-1.5" />}
                  {pulling ? "Pulling from SAP..." : onCooldown ? remainingLabel : "Pull Latest POs"}
                </Button>
                <Popover open={forceCheckOpen} onOpenChange={setForceCheckOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="text-xs text-[#5B738B] underline decoration-dotted underline-offset-2 hover:text-[#0F172A]"
                      data-testid="act-as-supplier-force-check-trigger"
                    >
                      Missing an open PO?
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-72 p-3" data-testid="act-as-supplier-force-check-popover">
                    <Label className="text-xs text-[#475569]">PO number</Label>
                    <div className="flex items-center gap-2 mt-1.5">
                      <Input
                        placeholder="e.g. 30896"
                        value={forceCheckPoNumber}
                        onChange={(e) => setForceCheckPoNumber(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") forceCheckPo(); }}
                        className="h-8 text-xs rounded-sm font-data"
                        data-testid="act-as-supplier-force-check-input"
                      />
                      <Button
                        onClick={forceCheckPo}
                        disabled={forceChecking || !forceCheckPoNumber.trim()}
                        className="h-8 rounded-sm text-xs shrink-0"
                        data-testid="act-as-supplier-force-check-button"
                      >
                        {forceChecking ? <CircleNotch size={13} className="animate-spin" /> : "Check"}
                      </Button>
                    </div>
                    <div className="flex items-start gap-1.5 mt-2 text-[11px] text-[#667085]">
                      <WarningCircle size={13} className="shrink-0 mt-0.5" />
                      <span>If a PO is genuinely open in SAP but "Pull Latest POs" keeps missing it, check it directly here by PO number.</span>
                    </div>
                  </PopoverContent>
                </Popover>
                <div className="relative">
                  <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#475569]" />
                  <Input
                    placeholder="Search PO #, Item #, or description..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="h-8 pl-8 pr-2 w-72 text-[13px] rounded-sm border-[#E2E8F0]"
                    data-testid="act-as-supplier-po-search-input"
                  />
                </div>
                <Select value={siteFilter} onValueChange={setSiteFilter}>
                  <SelectTrigger className="h-8 w-40 text-[13px] rounded-sm border-[#E2E8F0]" data-testid="act-as-supplier-site-filter">
                    <SelectValue placeholder="Filter by Site" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" data-testid="act-as-supplier-site-filter-all">All Sites</SelectItem>
                    {sites.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`act-as-supplier-site-filter-${s}`}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-1.5 rounded-sm border border-[#E2E8F0] bg-white px-2 h-8" data-testid="act-as-supplier-date-filter">
                  <CalendarBlank size={14} className="text-[#475569] shrink-0" />
                  <DateField
                    value={fromDate}
                    onChange={setFromDate}
                    disabledMatcher={(d) => d > parseISO(toDate)}
                    testId="act-as-supplier-date-filter-from"
                    className="!h-6 !mt-0 !w-[104px] !border-none !px-1 !text-[12px]"
                  />
                  <span className="text-[#98A2B3] text-xs">to</span>
                  <DateField
                    value={toDate}
                    onChange={setToDate}
                    disabledMatcher={(d) => d < parseISO(fromDate)}
                    testId="act-as-supplier-date-filter-to"
                    className="!h-6 !mt-0 !w-[104px] !border-none !px-1 !text-[12px]"
                  />
                  {(fromDate !== isoDaysAgo(30) || toDate !== isoDaysAgo(0)) && (
                    <button
                      type="button"
                      onClick={() => { setFromDate(isoDaysAgo(30)); setToDate(isoDaysAgo(0)); }}
                      className="text-[11px] font-semibold text-[#1E40AF] hover:underline pl-1"
                      title="Reset to the last 30 days"
                      data-testid="act-as-supplier-date-filter-reset"
                    >
                      Reset
                    </button>
                  )}
                </div>
                <FreshnessPill lastSyncedAt={lastSyncedAt} isFresh={liveSync} testId="act-as-supplier-freshness-pill" />
              </div>
            </div>

            {posLoading && <div className="mt-4 text-sm text-[#475569]" data-testid="act-as-supplier-po-loading">Loading Purchase Orders...</div>}

            {!posLoading && filteredPos.length === 0 && (
              <div className="mt-4 bg-white border border-[#E2E8F0] rounded-sm shadow-sm p-6 text-center text-sm text-[#475569]" data-testid="act-as-supplier-po-empty">
                No open Purchase Orders for this supplier right now.
              </div>
            )}

            {!posLoading && filteredPos.length > 0 && (
              <div className="mt-3 bg-white border border-[#E2E8F0] rounded-sm shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] overflow-x-auto" data-testid="act-as-supplier-po-table">
                <table className="w-full text-xs border-collapse">
                  <thead className="bg-[#F1F5F9] text-[#344054] text-xs font-bold font-heading uppercase tracking-wide">
                    <tr>
                      <th className="border border-[#E2E8F0] p-1.5 w-8"></th>
                      <th className="border border-[#E2E8F0] p-1.5 text-left">PO Number</th>
                      <th className="border border-[#E2E8F0] p-1.5 text-left">PO Date</th>
                      <th className="border border-[#E2E8F0] p-1.5 text-left">Site</th>
                      <th className="border border-[#E2E8F0] p-1.5 text-left">Item Code</th>
                      <th className="border border-[#E2E8F0] p-1.5 text-left">Description</th>
                      <th className="border border-[#E2E8F0] p-1.5 text-right">Open Qty</th>
                      <th className="border border-[#E2E8F0] p-1.5 text-right">Item Price</th>
                      <th className="border border-[#E2E8F0] p-1.5 text-right w-28">Ship Qty</th>
                      <th className="border border-[#E2E8F0] p-1.5 text-left">SAP Verified</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPos.filter((po) => po.remaining_qty > 0).map((po, i) => {
                      const key = cartKey(po);
                      const inCart = !!cart[key];
                      const siteBlocked = !!lockedSiteId && po.ship_to_site_id && po.ship_to_site_id !== lockedSiteId;
                      return (
                        <tr key={i} className="bg-white odd:bg-[#F9FAFB]" data-testid={`act-as-supplier-po-row-${po.po_number}-${po.item_number}`}>
                          <td className="border border-[#E2E8F0] px-2 py-1">
                            <span title={!po.sap_verified_at ? "SAP has not confirmed this line's Open Quantity yet - try again in a few minutes" : undefined}>
                              <Checkbox
                                checked={inCart}
                                disabled={siteBlocked || !po.sap_verified_at}
                                onCheckedChange={(c) => toggleCartItem(po, !!c)}
                                data-testid={`act-as-supplier-po-checkbox-${po.po_number}-${po.item_number}`}
                              />
                            </span>
                          </td>
                          <td className="border border-[#E2E8F0] px-2 py-1 font-data">{po.po_number}</td>
                          <td className="border border-[#E2E8F0] px-2 py-1 font-data whitespace-nowrap">{po.po_date ? format(parseISO(po.po_date), "dd-MM-yyyy") : "—"}</td>
                          <td className="border border-[#E2E8F0] px-2 py-1">
                            {po.ship_to_site_id && (
                              <span
                                className={`inline-flex items-center px-1.5 py-0.5 rounded-sm text-[11px] font-data font-semibold ${siteBlocked ? "bg-[#FEE2E2] text-[#991B1B]" : "bg-[#DCFCE7] text-[#166534]"}`}
                                title={siteBlocked ? `Locked to Site ${lockedSiteId} - remove those items first to ship to ${po.ship_to_site_id} instead` : undefined}
                                data-testid={`act-as-supplier-po-site-badge-${po.po_number}-${po.item_number}`}
                              >
                                {po.ship_to_site_id}
                              </span>
                            )}
                          </td>
                          <td className="border border-[#E2E8F0] px-2 py-1 font-data text-xs">{po.product_id || "—"}</td>
                          <td className="border border-[#E2E8F0] px-2 py-1">{po.description || "-"}</td>
                          <td className="border border-[#E2E8F0] px-2 py-1 text-right font-data">
                            {po.remaining_qty} {po.unit_of_measure}
                            {po.extra_tolerance_pct > 0 && (
                              <div className="text-[10px] text-[#166534] font-sans font-semibold whitespace-nowrap" data-testid={`act-as-supplier-po-tolerance-badge-${po.po_number}-${po.item_number}`} title="This PO's source PR was approved with an Extra % over-delivery allowance - you can ship above Open Qty up to this limit.">
                                +{po.extra_tolerance_pct}% extra OK
                              </div>
                            )}
                          </td>
                          <td className="border border-[#E2E8F0] px-2 py-1 text-right font-data text-[#475467]" data-testid={`act-as-supplier-po-price-${po.po_number}-${po.item_number}`}>
                            {po.unit_price != null ? [po.currency, fmtUnitPrice(po.unit_price)].filter(Boolean).join(" ") : "-"}
                          </td>
                          <td className="border border-[#E2E8F0] px-2 py-1 text-right">
                            <Input
                              type="number"
                              disabled={!inCart}
                              value={inCart ? cart[key].ship_qty : ""}
                              onChange={(e) => updateCartQty(key, e.target.value)}
                              className="h-7 w-24 text-right rounded-sm border-[#E2E8F0] text-xs"
                              data-testid={`act-as-supplier-po-qty-input-${po.po_number}-${po.item_number}`}
                            />
                          </td>
                          <td className="border border-[#E2E8F0] px-2 py-1 whitespace-nowrap">
                            <div className={`text-[10px] font-sans flex items-center gap-1 ${po.sap_verified_at ? "text-[#94A3B8]" : "text-[#B54708] font-semibold"}`} data-testid={`act-as-supplier-po-sap-verified-${po.po_number}-${po.item_number}`}>
                              {po.sap_verified_at ? (
                                <>
                                  <CheckCircle size={12} weight="fill" className="text-[#12B76A]" />
                                  SAP-verified {timeAgo(po.sap_verified_at)}
                                </>
                              ) : (
                                <>
                                  <Clock size={12} weight="fill" />
                                  Not yet verified - can't ship
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {cartItems.length > 0 && (
              <div className="fixed bottom-0 left-0 right-0 md:left-60 bg-white border-t border-[#E2E8F0] shadow-[0_-2px_8px_0_rgba(16,24,40,0.08)] px-4 py-3 flex items-center justify-between z-20" data-testid="act-as-supplier-cart-bar">
                <span className="text-sm text-[#0F172A] font-semibold" data-testid="act-as-supplier-cart-count">{cartItems.length} item{cartItems.length > 1 ? "s" : ""} selected</span>
                <div className="flex items-center gap-2">
                  <Button onClick={() => setCart({})} variant="outline" className="rounded-sm border-[#DC2626] text-[#DC2626] hover:bg-[#FEF2F2] hover:text-[#DC2626] transition-colors duration-150" data-testid="act-as-supplier-clear-selections-button">
                    <X size={14} className="mr-1" /> Clear Selections
                  </Button>
                  <Button onClick={() => setReviewOpen(true)} className="rounded-sm bg-[#1E40AF] hover:bg-[#1E3A8A] transition-colors duration-150" data-testid="act-as-supplier-review-shipment-button">
                    <Truck size={14} className="mr-1" /> Review Shipment
                  </Button>
                </div>
              </div>
            )}

            <div className="mt-8 bg-white border border-[#CBD3DB] rounded-sm shadow-sm p-5 max-w-lg" data-testid="act-as-supplier-reset-password-card">
              <div className="flex items-center gap-2 mb-3">
                <Key size={16} weight="fill" className="text-[#0076CC]" />
                <h2 className="font-sans text-sm font-bold text-[#111827]">Reset Password for {selectedAccount.company_name}</h2>
              </div>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="act-as-supplier-new-password" className="text-xs text-[#5B738B]">New Password</Label>
                  <Input
                    id="act-as-supplier-new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    className="rounded-sm border-[#CBD3DB] mt-1"
                    data-testid="act-as-supplier-new-password-input"
                  />
                </div>
                <div>
                  <Label htmlFor="act-as-supplier-confirm-password" className="text-xs text-[#5B738B]">Confirm Password</Label>
                  <Input
                    id="act-as-supplier-confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-type the password"
                    className="rounded-sm border-[#CBD3DB] mt-1"
                    data-testid="act-as-supplier-confirm-password-input"
                  />
                </div>
                <p className="text-[11px] text-[#98A2B3]">The new password is emailed directly to {selectedAccount.email}.</p>
                <Button
                  onClick={resetPassword}
                  disabled={resetting || !newPassword || !confirmPassword}
                  className="rounded-sm bg-[#0076CC] hover:bg-[#005A9E] transition-colors duration-150"
                  data-testid="act-as-supplier-reset-password-button"
                >
                  {resetting ? "Setting & Emailing..." : "Set & Email New Password"}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="rounded-sm max-w-3xl" data-testid="act-as-supplier-review-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Review Shipment (on behalf of {selectedAccount?.company_name})</DialogTitle>
            <DialogDescription>Confirm the items and quantities before generating the shipment code.</DialogDescription>
          </DialogHeader>
          <div className="max-h-72 overflow-y-auto border border-[#E2E8F0] rounded-sm">
            <table className="w-full text-[11px]">
              <thead className="bg-[#F8FAFC] sticky top-0">
                <tr>
                  <th className="border-b border-[#E2E8F0] p-1.5 text-left">PO / Item</th>
                  <th className="border-b border-[#E2E8F0] p-1.5 text-left">Description</th>
                  <th className="border-b border-[#E2E8F0] p-1.5 text-right">Open Qty</th>
                  <th className="border-b border-[#E2E8F0] p-1.5 text-right">Item Price</th>
                  <th className="border-b border-[#E2E8F0] p-1.5 text-right">Ship Qty</th>
                  <th className="border-b border-[#E2E8F0] p-1.5 text-right">Amount</th>
                  <th className="border-b border-[#E2E8F0] p-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {cartItems.map((c) => {
                  const amount = c.unit_price != null ? c.unit_price * (parseFloat(c.ship_qty) || 0) : null;
                  return (
                    <tr key={c.key} className="border-t border-[#E2E8F0]" data-testid={`act-as-supplier-review-row-${c.key}`}>
                      <td className="px-1.5 py-1.5 font-data whitespace-nowrap">PO {c.po_number} · {c.item_number}</td>
                      <td className="px-1.5 py-1.5">{c.description || "—"}</td>
                      <td className="px-1.5 py-1.5 text-right font-data text-[#475569]">
                        {c.remaining_qty ?? "—"} {c.unit_of_measure}
                        {c.extra_tolerance_pct > 0 && (
                          <div className="text-[10px] text-[#166534] font-sans font-semibold" data-testid={`act-as-supplier-review-tolerance-badge-${c.key}`}>+{c.extra_tolerance_pct}% extra OK</div>
                        )}
                      </td>
                      <td className="px-1.5 py-1.5 text-right font-data text-[#475569] whitespace-nowrap" data-testid={`act-as-supplier-review-price-${c.key}`}>
                        {c.unit_price != null ? [c.currency, fmtUnitPrice(c.unit_price)].filter(Boolean).join(" ") : "-"}
                      </td>
                      <td className="px-1.5 py-1.5">
                        <div className="flex items-center justify-end gap-1">
                          <Input type="number" value={c.ship_qty} onChange={(e) => updateCartQty(c.key, e.target.value)} className="h-8 w-20 text-right rounded-sm border-[#E2E8F0] text-xs" data-testid={`act-as-supplier-review-qty-input-${c.key}`} />
                          <span className="text-[#475569] w-8">{c.unit_of_measure}</span>
                        </div>
                      </td>
                      <td className="px-1.5 py-1.5 text-right font-data font-semibold whitespace-nowrap" data-testid={`act-as-supplier-review-amount-${c.key}`}>
                        {amount != null ? fmtMoney(amount, c.currency) : "-"}
                      </td>
                      <td className="px-1.5 py-1.5 text-center">
                        <button onClick={() => removeFromCart(c.key)} className="text-[#991B1B] hover:bg-[#991B1B]/10 rounded-sm p-1" data-testid={`act-as-supplier-review-remove-${c.key}`}>
                          <X size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-[#CBD3DB] bg-[#F8FAFC]">
                  <td colSpan={5} className="px-1.5 py-2 text-right font-semibold text-[#344054]">Total</td>
                  <td className="px-1.5 py-2 text-right font-data font-bold text-[#1E40AF]" data-testid="act-as-supplier-review-total">{fmtMoney(cartTotal, cartCurrency)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setReviewOpen(false)}>Cancel</Button>
            <Button onClick={() => setConfirmOpen(true)} disabled={cartItems.length === 0} className="rounded-sm bg-[#1E40AF] hover:bg-[#1E3A8A] transition-colors duration-150" data-testid="act-as-supplier-review-confirm-button">
              Confirm Shipment
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="rounded-sm" data-testid="act-as-supplier-confirm-generate-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Generate Shipment Code?</DialogTitle>
            <DialogDescription>
              This creates one shipment covering {cartItems.length} item{cartItems.length > 1 ? "s" : ""} on behalf of {selectedAccount?.company_name} - flagged with your name for traceability. Are you sure?
            </DialogDescription>
          </DialogHeader>
          {submitError && <div className="text-sm text-[#991B1B]" data-testid="act-as-supplier-submit-error">{submitError}</div>}
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button onClick={submitShipment} disabled={submitting} className="rounded-sm bg-[#1E40AF] hover:bg-[#1E3A8A] transition-colors duration-150" data-testid="act-as-supplier-confirm-generate-button">
              {submitting ? "Generating..." : "Yes, Generate Code"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!successCode} onOpenChange={(o) => !o && setSuccessCode(null)}>
        <DialogContent className="rounded-sm" data-testid="act-as-supplier-success-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Shipment Created</DialogTitle>
            <DialogDescription>Share this code with {selectedAccount?.company_name} for their shipment paperwork - it's how the warehouse matches it on arrival.</DialogDescription>
          </DialogHeader>
          <div className="text-center py-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-sm">
            <div className="text-3xl font-data font-bold tracking-widest text-[#1E40AF]" data-testid="act-as-supplier-success-code">{successCode}</div>
          </div>
          <Button onClick={() => setSuccessCode(null)} className="rounded-sm bg-[#1E40AF] hover:bg-[#1E3A8A] transition-colors duration-150" data-testid="act-as-supplier-success-close-button">Done</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
